import invariant from 'invariant';
import { Dispatch } from 'redux';

import { ApiSearchResults } from 'api/search';
import {
  ApiFilestackImageData,
  ApiFilestackImageWithTextData,
} from 'api/types/ApiFilestackImageData';
import { fetchCmsApiAuthor } from 'modules/cms/api/cmsApiAuthorEndpoints';
import { cmsApiFetchTag } from 'modules/cms/api/cmsApiTagEndpoints';
import algoliaSearch from 'utils/algoliaSearch';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { arrToObject, filterObject, isEmpty } from 'utils/functional';
import { getJson } from 'utils/http';
import getPostSearchIndex from 'utils/search/getPostSearchIndex';
import getSearchConfig from 'utils/search/getSearchConfig';
import { postSearchFacetQueryFilters } from 'utils/search/getSearchQueryOptions';
import { setFiltersForPostSearch } from 'utils/search/searchFacets';
import { truncateToUTF8Bytes } from 'utils/string';

// Types

type DynamicFacet = {
  fetchFn: () => Promise<unknown>;
  facetKey: PostSearchFacetKeys;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  responseToOptions: ({ categories }: any) => any;
};

type SetQueryAction = {
  type: 'POST_SEARCH_SET_QUERY';
  query: string;
};

type SearchSuccessAction = {
  type: 'POST_SEARCH_RESULTS_SUCCESS';
  results: MainStorePostSearchStateResults;
};

type SearchStartedAction = {
  type: 'POST_SEARCH_STARTED';
};

type SetPageIndexAction = {
  type: 'POST_SEARCH_SET_PAGE_INDEX';
  pageIndex: number;
};

type InitializeSearchAction = {
  type: 'POST_SEARCH_INITIALIZE';
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryParams: Record<string, any>;
};

type UninitializeSearchAction = {
  type: 'POST_SEARCH_UNINITIALIZE';
};

type ClearSearchResultsAction = {
  type: 'POST_SEARCH_CLEAR_RESULTS';
};

type ResetPageAction = {
  type: 'POST_SEARCH_RESET_PAGE';
};

type ResetFiltersAction = {
  type: 'POST_SEARCH_RESET_FILTERS';
};

type SetSearchFilter = {
  type: 'POST_SEARCH_SET_FILTER';
  key: string;
  value: string | null | undefined;
};

type SelectFilterOption = {
  type: 'POST_SEARCH_SELECT_FILTER_OPTION';
  key: string;
  option: string | null | undefined;
};

type DeselectFilterOption = {
  type: 'POST_SEARCH_DESELECT_FILTER_OPTION';
  key: string;
  option: string | null | undefined;
};

type SelectAllFilterOption = {
  type: 'POST_SEARCH_SELECT_ALL_FILTER_OPTIONS';
  key: PostSearchFacetKeys;
};

type SetFacetOptions = {
  type: 'POST_SEARCH_SET_FACET_OPTIONS';
  facetKey: PostSearchFacetKeys;
  options: Array<string>;
  optionTitles: Record<string, string>;
  optionsArray: Array<{
    id: string;
    parentId: string | null | undefined;
    icon: ApiFilestackImageData;
    slug: string;
    name: string;
  }>;
};

export type MainStorePostSearchAction =
  | SetQueryAction
  | SearchSuccessAction
  | SearchStartedAction
  | SetPageIndexAction
  | InitializeSearchAction
  | UninitializeSearchAction
  | ClearSearchResultsAction
  | ResetPageAction
  | ResetFiltersAction
  | SelectAllFilterOption
  | DeselectFilterOption
  | SetSearchFilter
  | SelectFilterOption
  | SetFacetOptions;

export type MainStorePostSearchStatePostHit = {
  author:
    | {
        imageHandle: string | null | undefined;
        name: string;
        slug: string;
      }
    | null
    | undefined;
  categories: Array<string>;
  content: string;
  headerImage: ApiFilestackImageWithTextData;
  locale: UserLocale;
  objectID: string;
  postedDate: string;
  showDate: boolean;
  subsiteId: Array<string>;
  subsiteName: string;
  tags: Array<string>;
  thumbnail: ApiFilestackImageWithTextData;
  title: string;
  url: string;
  type: string;
  metaDescription: string | null | undefined;
};

export type MainStorePostSearchStateResults = Omit<ApiSearchResults, 'hits'> & {
  hits: Array<MainStorePostSearchStatePostHit>;
};

export type PostSearchFacetKeys =
  | 'articleAuthorFacet'
  | 'articleTagsFacet'
  | 'articleCategoriesFacet';

type PostSearchFilters = {
  articleAuthorFacet: string[];
  articleTagsFacet: string[];
  articleCategoriesFacet: string[];
};

const postSearchFacets: Record<PostSearchFacetKeys, SearchFacet> = {
  articleCategoriesFacet: {
    id: 'articleCategoriesFacet',
    name: 'categories',
    title: getText('Category'),
    type: 'array',
    lazy: true,
    optionsArray: [],
    options: [],
    // Lazy loaded
    optionTitles: {}, // Lazy loaded
  },
  articleAuthorFacet: {
    id: 'articleAuthorFacet',
    name: 'author.slug',
    title: getText('Author'),
    type: 'array',
    lazy: true,
    options: [],
    optionTitles: {},
  },
  articleTagsFacet: {
    id: 'articleTagsFacet',
    name: 'tags.slug',
    title: getText('Tags'),
    type: 'array',
    lazy: true,
    options: [],
    optionTitles: {},
  },
};

const initialFacetsThatArentLoaded: Array<string> = Object.keys(
  filterObject(postSearchFacets, (_, f) => Boolean(f.lazy)),
);

export type MainStorePostSearchState = {
  query: string;
  pageIndex: number;
  lastLoadedPageIndex: number;
  searchId: number;
  // Incrementing id
  resultsByPage: Record<string, MainStorePostSearchStateResults>;
  resultsForSearchId?: string | null | undefined;
  initialized: boolean;
  initialSearchStarted: boolean;
  initialResultsLoaded: boolean;
  isSearching: boolean;
  queryID: string | null | undefined;
  searchFacets: Record<PostSearchFacetKeys, SearchFacet>;
  facetsThatArentLoaded: Array<string>;
  filters: PostSearchFilters;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postSearch?: any;
};

const initialSearchState: MainStorePostSearchState = {
  query: '',
  pageIndex: 0,
  lastLoadedPageIndex: 0,
  searchId: 0,
  // Incrementing id
  resultsByPage: {},
  initialized: false,
  initialSearchStarted: false,
  initialResultsLoaded: false,
  isSearching: false,
  queryID: null,
  searchFacets: postSearchFacets,
  filters: {
    articleAuthorFacet: [],
    articleTagsFacet: [],
    articleCategoriesFacet: [],
  },
  facetsThatArentLoaded: initialFacetsThatArentLoaded,
};

// Reducer

const defaultState: MainStorePostSearchState = initialSearchState;

export function postSearchReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: MainStorePostSearchState = defaultState,
  action: MainStorePostSearchAction,
) {
  switch (action.type) {
    case 'POST_SEARCH_SET_QUERY': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        query: action.query,
      };
    }

    case 'POST_SEARCH_RESET_PAGE': {
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0, // New search, reset page
      };
    }

    case 'POST_SEARCH_RESET_FILTERS': {
      const filters = {
        ...setFiltersForPostSearch(state.searchFacets, {}),
        articleCategoriesFacet: state.filters.articleCategoriesFacet,
      };
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        landingPagePath: null,
        filters,
      };
    }

    case 'POST_SEARCH_SET_PAGE_INDEX': {
      return { ...state, pageIndex: action.pageIndex };
    }

    case 'POST_SEARCH_INITIALIZE': {
      const { page, pq } = action.queryParams;
      const authorSlug = action.queryParams['author.slug'];
      const tagsSlug = action.queryParams['tags.slug'];

      const facetsThatArentLoaded = initialFacetsThatArentLoaded.filter(
        (facet) =>
          (facet === 'articleTagsFacet' && tagsSlug) ||
          (facet === 'articleAuthorFacet' && authorSlug) ||
          facet === 'articleCategoriesFacet',
      );

      const filters: PostSearchFilters = setFiltersForPostSearch(
        postSearchFacets,
        action.queryParams,
      );
      return {
        ...initialSearchState,
        facetsThatArentLoaded,
        initialized: true,
        query: pq ? truncateToUTF8Bytes(pq || '', 512) : state.query,
        pageIndex: page ? Number(page) - 1 : state.pageIndex,
        filters,
      };
    }

    case 'POST_SEARCH_UNINITIALIZE': {
      return {
        ...state,
        facetsThatArentLoaded: initialFacetsThatArentLoaded,
        initialized: false,
      };
    }

    case 'POST_SEARCH_STARTED': {
      return {
        ...state,
        initialSearchStarted: true,
        isSearching: true,
        queryID: null,
      };
    }

    case 'POST_SEARCH_RESULTS_SUCCESS': {
      // If the results are from an older search, ignore them
      const lastResults =
        state.resultsByPage[state.lastLoadedPageIndex.toString()];

      if (lastResults && lastResults.timestamp > action.results.timestamp) {
        return state;
      }

      const newSearch = action.results.searchId !== state.resultsForSearchId;
      return {
        ...state,
        queryID: action.results.queryID,
        resultsByPage: newSearch
          ? {
              [action.results.page]: action.results,
            }
          : { ...state.resultsByPage, [action.results.page]: action.results },
        lastLoadedPageIndex: action.results.page,
        resultsForSearchId: action.results.searchId,
        initialResultsLoaded: true,
        isSearching: false,
      };
    }

    case 'POST_SEARCH_CLEAR_RESULTS': {
      return { ...state, resultsByPage: {}, resultsForSearchId: null };
    }

    case 'POST_SEARCH_SET_FACET_OPTIONS': {
      const { options, optionTitles, optionsArray, facetKey } = action;
      const facet = state.searchFacets[facetKey];
      const facetsThatArentLoaded: Array<string> =
        state.facetsThatArentLoaded.filter((key) => key !== facetKey);

      if (!facet && isEmpty(options)) {
        return state;
      }

      // const sideFacets = sideFacetsForType(searchFacets, state.filters.type);
      // const hasFacet = sideFacets.find(f => f.id === facet.id);
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const filterValue = facet.id && state.filters[facet.id];
      const updatedFilters = facet.id
        ? { ...state.filters, [facet.id]: filterValue || options }
        : state.filters;
      const newSearchFacets: Record<string, SearchFacet> = {
        // Consider all search facets, not just ones in the current state
        ...postSearchFacets,
        ...state.searchFacets,
        [facetKey]: {
          ...facet,
          options,
          optionTitles,
          optionsArray,
        },
      };
      return {
        ...state,
        // This needs to get bumped if we change filter values
        // But if we dont, then it doesnt need to get changed
        searchId:
          filterValue && filterValue !== options
            ? state.searchId + 1
            : state.searchId,
        facetsThatArentLoaded,
        // Only set the filter value if the current filter.type supports this
        // facet
        filters: updatedFilters,
        searchFacets: newSearchFacets,
      };
    }

    case 'POST_SEARCH_SET_FILTER': {
      const facet = action.key;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      const value = action.value;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-object-spread
      const filters = Object.assign(
        {}, // Setting the type filter resets the other filters.
        state.filters,
        {
          [facet]: value,
        },
      );
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters,
      };
    }

    case 'POST_SEARCH_SELECT_FILTER_OPTION': {
      const facet = action.key;
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const filter = state.filters[facet];
      const selectedOptions = [
        ...((Array.isArray(filter) && filter) || []),
        action.option,
      ];
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters: { ...state.filters, [facet]: selectedOptions },
      };
    }

    case 'POST_SEARCH_DESELECT_FILTER_OPTION': {
      const facet = action.key;
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      const option = action.option;
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      invariant(Array.isArray(state.filters[facet]), 'Missing search filter');
      // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      const selectedOptions = (state.filters[facet] as string[]).filter(
        (v) => v !== option,
      );
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters: { ...state.filters, [facet]: selectedOptions },
      };
    }

    case 'POST_SEARCH_SELECT_ALL_FILTER_OPTIONS': {
      const facet = action.key;
      return {
        ...state,
        searchId: state.searchId + 1,
        pageIndex: 0,
        // New search, reset page
        filters: {
          ...state.filters,
          [facet]: state.searchFacets[facet].options,
        },
      };
    }

    default:
      return state;
  }
}

// Utility operations on state

export const resultsFn = (state: MainStorePostSearchState) =>
  state.resultsByPage[state.pageIndex.toString()];

const executeSearchQuery = (
  {
    state: {
      postSearch: {
        pageIndex: pageIndexState,
        query,
        searchId,
        filters,
        searchFacets,
      },
    },
    pageIndex,
    subsiteId,
    includedSubsiteIds,
    locale,
  }: {
    state: { postSearch: MainStorePostSearchState };
    pageIndex?: number;
    subsiteId: string;
    includedSubsiteIds: Array<string>;
    locale: string;
  }, // TODO: Fix this the next time the file is edited.
): // eslint-disable-next-line @typescript-eslint/no-explicit-any
Promise<any> => {
  const timestamp = new Date();
  let index = getPostSearchIndex();

  // use replica that's sorted differently when query is provided by user input
  if (query && query.length > 0 && index !== null && index !== undefined) {
    index = `${index}-relevant`;
  }

  invariant(index, 'search index not retreived');
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-param-reassign
  pageIndex = pageIndex || pageIndexState;
  const searchFilters = postSearchFacetQueryFilters(
    filters,
    searchFacets,
    locale,
  );
  const subsitesToSearch = [...includedSubsiteIds, subsiteId].map(
    (id) => `subsiteId:${id}`,
  );
  const queryOptions: QueryOptions = {
    index,
    hitsPerPage: 20,
    page: pageIndex || 0,
    facets: ['*', 'subsiteId', 'pageType', 'categoryIds', 'locale'],
    facetFilters: [subsitesToSearch],
    filters: searchFilters.join(' AND '),
    attributesToSnippet: ['description:20'],
    attributesToRetrieve: ['*'],
    // TODO: list attributes,
    removeStopWords: true,
    ignorePlurals: true,
    advancedSyntax: true,
    queryLanguages: [locale],
  };
  const searchConfig = getSearchConfig();
  return algoliaSearch(searchConfig, query, queryOptions).then((results) => ({
    ...results,
    timestamp,
    searchId,
    // This is for analytics
    searchParameters: {
      query,
      queryOptions,
    },
  }));
};

export const setSearchQuery =
  (query: string, didUserChange: boolean) => (dispatch: Dispatch) => {
    if (didUserChange) {
      trackEvent('Articles: Changed Search Query', {
        new_query: query, // TODO: add tracking data for POST
      });
    }

    dispatch({
      type: 'POST_SEARCH_SET_QUERY',
      query,
    });
  };

export const loadDynamicFacetOptions =
  ({
    authorSlug,
    subsiteId,
    tagsSlug,
  }: {
    authorSlug?: string;
    subsiteId: string;
    tagsSlug?: string;
  }) =>
  (
    dispatch: Dispatch,
    getState: () => { postSearch: MainStorePostSearchState },
  ) => {
    const state = getState();
    const facetsThatArentLoaded = state.postSearch.facetsThatArentLoaded.filter(
      (facet) =>
        (facet === 'articleTagsFacet' && tagsSlug) ||
        (facet === 'articleAuthorFacet' && authorSlug) ||
        facet === 'articleCategoriesFacet',
    );
    const dynamicFacets: DynamicFacet[] = [
      {
        fetchFn: () =>
          getJson(
            `/data/website/subsites/${subsiteId}/categories?lang=${CURRENT_LOCALE}`,
          ),
        facetKey: 'articleCategoriesFacet',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        responseToOptions: ({ categories }: any) => categories,
      },
    ];

    if (authorSlug) {
      dynamicFacets.push({
        fetchFn: () => fetchCmsApiAuthor(authorSlug || ''),
        facetKey: 'articleAuthorFacet',
        responseToOptions: (author) => [author],
      });
    }

    if (tagsSlug) {
      dynamicFacets.push({
        fetchFn: () => cmsApiFetchTag(tagsSlug || '', subsiteId),
        facetKey: 'articleTagsFacet',
        responseToOptions: (tag) => [tag],
      });
    }

    return Promise.all(
      dynamicFacets.map(({ facetKey, fetchFn, responseToOptions }) => {
        if (!facetsThatArentLoaded.includes(facetKey)) {
          return Promise.resolve();
        }

        return fetchFn()
          .then((response) => responseToOptions(response))
          .then((options) => {
            dispatch({
              type: 'POST_SEARCH_SET_FACET_OPTIONS',
              facetKey,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              options: options.map((f: any) => f.slug),
              optionTitles: arrToObject(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                options.map(({ slug, name }: any) => [slug, name]),
              ),
              optionsArray: options.map(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ({ slug, id, icon, parentId, name }: any) => ({
                  id,
                  name,
                  icon,
                  slug,
                  parentId,
                }),
              ),
            });
          });
      }),
    );
  };

export const initializeSearch =
  // @ts-expect-error TS(2739): Type '{}' is missing the following properties from... Remove this comment to see the full error message


    ({
      queryParams,
      subsiteId,
    }: {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      queryParams: Record<string, any>;
      subsiteId: string;
    } = {}) =>
    (dispatch: Dispatch) =>
      Promise.all([
        dispatch({
          type: 'POST_SEARCH_INITIALIZE',
          queryParams,
        }),
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dispatch<any>(
          loadDynamicFacetOptions({
            subsiteId,
            authorSlug: queryParams['author.slug'],
            tagsSlug: queryParams['tags.slug'],
          }),
        ),
      ]);

export const uninitializeSearch = () => (dispatch: Dispatch) =>
  dispatch({
    type: 'POST_SEARCH_UNINITIALIZE',
  });

export const setPageIndex =
  (pageIndex: number, didUserChange: boolean) => (dispatch: Dispatch) => {
    if (didUserChange) {
      trackEvent('POST: Changed Search Page Number', {
        new_page: pageIndex, // TODO: tracking data
      });
    }

    dispatch({
      type: 'POST_SEARCH_SET_PAGE_INDEX',
      pageIndex,
    });
  };

const searchResultsSuccessful = ({
  results,
}: {
  results: MainStorePostSearchStateResults;
}): SearchSuccessAction => ({
  type: 'POST_SEARCH_RESULTS_SUCCESS',
  results,
});

export const search =
  (subsiteId: string, includedSubsiteIds: Array<string>, locale: string) =>
  (
    dispatch: Dispatch,
    getState: () => { postSearch: MainStorePostSearchState },
  ) => {
    dispatch({
      type: 'POST_SEARCH_STARTED',
    });
    executeSearchQuery({
      state: getState(),
      subsiteId,
      includedSubsiteIds,
      locale,
    }).then((results) => {
      dispatch(
        searchResultsSuccessful({
          results,
        }),
      );
    });
  };

export const setSearchFilter = (
  filterKey: string,
  value: string | null | undefined,
): SetSearchFilter => {
  if (filterKey === 'type') {
    invariant(value, 'type can never be set to undefined/null');
  }

  return {
    type: 'POST_SEARCH_SET_FILTER',
    key: filterKey,
    value,
  };
};

export const selectSearchFilterOption = (
  filterKey: string,
  option: string | null | undefined,
): SelectFilterOption => ({
  type: 'POST_SEARCH_SELECT_FILTER_OPTION',
  key: filterKey,
  option,
});

export const deselectSearchFilterOption = (
  filterKey: string,
  option: string | null | undefined,
): DeselectFilterOption => ({
  type: 'POST_SEARCH_DESELECT_FILTER_OPTION',
  key: filterKey,
  option,
});

export const selectAllSearchFilterOptions = (
  filterKey: PostSearchFacetKeys,
): SelectAllFilterOption => ({
  type: 'POST_SEARCH_SELECT_ALL_FILTER_OPTIONS',
  key: filterKey,
});

export const resetSearchFilters = (): ResetFiltersAction => ({
  type: 'POST_SEARCH_RESET_FILTERS',
});
